---
title: "Среднее или медиана: что выбрать, если в данных есть выбросы"
description: "Как сравнить среднее и медиану, заметить влияние крайних значений и выбрать показатель для зарплат, сроков, цен и других данных."
locale: "ru"
date: "2026-07-23"
tags: ["среднее", "медиана", "выбросы"]
relatedTools: ["average-median-calculator"]
translations:
  en: "mean-or-median-which-to-use"
faq:
  - q: "Что лучше показывает типичное значение: среднее или медиана?"
    a: "При заметных выбросах и длинном хвосте распределения медиана обычно ближе к типичному наблюдению. Среднее полезнее, когда важен итог по всем значениям или крайние значения нельзя исключать из задачи."
  - q: "Как найти медиану, если чисел чётное количество?"
    a: "Отсортируйте ряд и найдите среднее арифметическое двух центральных значений. Например, медиана ряда 2, 4, 7, 9 равна 5,5."
  - q: "Большая разница между средним и медианой доказывает наличие выброса?"
    a: "Нет. Разница служит поводом посмотреть на распределение, минимум, максимум и исходные значения, но сама по себе не доказывает статистический выброс."
---

Если нужно описать **типичное наблюдение**, начните с медианы, когда в списке
есть единичные очень большие или очень маленькие значения. Если нужно оценить
**общий объём**, который затем делится между всеми наблюдениями, чаще требуется
среднее арифметическое.

| Задача | Что смотреть первым |
| --- | --- |
| Типичная зарплата, цена квартиры, срок ответа | Медиану |
| Общая нагрузка на одного сотрудника или средний расход | Среднее |
| Однородные измерения без заметных крайних значений | Среднее и медиану вместе |
| Небольшая выборка с подозрительным значением | Исходный ряд, минимум и максимум, затем оба показателя |

Вставьте значения в [калькулятор среднего и медианы](/srednee-mediana): он
покажет оба результата, количество, сумму, минимум, максимум и размах. Такой
набор полезнее одного числа, потому что сразу виден масштаб крайних значений.

## Как один выброс меняет ответ

Возьмём пять значений:

`48, 52, 55, 57, 310`

Среднее равно `104,4`, потому что сумма `522` делится на пять. Медиана равна
`55`: после сортировки это центральное число. Если значения — сроки выполнения
обычных задач в минутах, медиана лучше описывает типичный случай, а `310`
выглядит как отдельный долгий эпизод.

Но среднее здесь не ошибочно. При планировании суммарного рабочего времени
долгий эпизод действительно потребляет ресурс. Если умножить медиану на число
задач, получится `275` минут вместо фактических `522`. Для ответа «сколько
времени нужно выделить в среднем на каждую задачу» крайнее значение нельзя
просто забыть.

Отсюда рабочее правило:

- медиана отвечает, где находится середина упорядоченного ряда;
- среднее учитывает вклад каждого значения в общую сумму;
- выбор зависит от вопроса, а не от того, какое число выглядит приятнее.

## Когда медиана полезнее

Медиана устойчива к единичным крайним значениям. Она удобна для цен, зарплат,
времени ожидания, длительности заявок и других данных, где большинство
наблюдений собрано рядом, а небольшой хвост уходит далеко вправо или влево.

Например, фраза «медианная цена — 6 млн» означает, что как минимум половина
значений не выше этой границы и как минимум половина не ниже. Это не то же
самое, что «большинство объектов стоит ровно 6 млн», и не оценка общей стоимости
всех объектов.

Для чётного количества чисел после сортировки берут два центральных значения и
считают их среднее. В ряду `2, 4, 7, 9` центральные числа — `4` и `7`, поэтому
медиана равна `5,5`.

## Когда среднее нельзя заменять медианой

Среднее нужно, если итоговая сумма имеет физический или финансовый смысл.
Примеры:

- объём обращений, который должна обработать команда;
- суммарный расход материалов на партию;
- общее время работы оборудования;
- средний балл при заранее установленном правиле расчёта.

Крайнее значение может оказаться не ошибкой, а редким, но реальным сценарием.
Удалять его только ради более ровного результата нельзя. Сначала проверьте
источник: не перепутаны ли единицы, не добавлен ли лишний ноль, относится ли
наблюдение к тому же периоду и той же группе.

## Можно ли найти выброс по разнице между средним и медианой

Большая разница — полезный сигнал для проверки, но не готовый тест на выбросы.
Несимметричное распределение может быть естественным: например, срок не бывает
отрицательным, зато иногда оказывается очень большим. И наоборот, среднее и
медиана могут совпасть даже при необычных крайних значениях, если они
уравновешивают друг друга.

Проверьте минимум, максимум и размах, затем вернитесь к исходному списку. Для
формального поиска выбросов нужны дополнительные правила и контекст задачи;
калькулятор считает описательные показатели, но не объявляет отдельные числа
ошибочными.

## Как проверить список без ручного пересчёта

1. Скопируйте один числовой столбец из таблицы или документа.
2. Вставьте его в [калькулятор](/srednee-mediana). Поддерживаются пробелы,
   новые строки, табы, точки с запятой и однозначно распознаваемые десятичные
   точки или запятые.
3. Проверьте количество принятых значений и список нераспознанных элементов.
   Подпись столбца или случайный текст не должны незаметно попасть в расчёт.
4. Сравните среднее с медианой, а затем посмотрите на минимум, максимум и
   размах.
5. Запишите в выводе не только число, но и выбранный показатель: «медиана
   срока», а не просто «среднее значение».

Перед публикацией результата сохраните исходный список и причину выбора
показателя. Это позволит отличить осмысленную медиану от удобного числа,
выбранного уже после расчёта.
